import { DragDropContext, Draggable } from "react-beautiful-dnd";
import { Plus } from "lucide-react";

import StrictModeDroppable from "../../StrictModeDroppable";
import EntryCard from "./EntryCard";
import { STATUS_COLUMNS, entryBoardStatus } from "../../../constants/taskboardStatus";

// Board view (product rule 1): four status columns with drag-and-drop to
// change status, over the SAME entry set as the timeline. Reuses the
// repo's proven react-beautiful-dnd + StrictModeDroppable stack (the
// same pattern as ActivePlayers.js). Dropping a card in another column
// calls onStatusChange, which persists immediately in the parent.
const BoardView = ({ entries, onStatusChange, onEdit, onDelete, onOpenThread, onAddEntry, canModify }) => {
  const byStatus = STATUS_COLUMNS.reduce((acc, c) => {
    acc[c.id] = [];
    return acc;
  }, {});
  entries.forEach((e) => byStatus[entryBoardStatus(e)].push(e));

  const handleDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId) return;
    const entry = entries.find((e) => String(e.id) === String(draggableId));
    if (entry) onStatusChange && onStatusChange(entry, destination.droppableId);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 items-start">
        {STATUS_COLUMNS.map((col) => {
          const colEntries = byStatus[col.id];
          return (
            <div
              key={col.id}
              className="rounded-xl p-2.5 flex flex-col gap-2"
              style={{
                background: col.columnBg,
                boxShadow: col.highlighted ? "inset 0 0 0 1.5px #B8D4FF" : "none",
              }}
            >
              <div className="flex items-center gap-2 px-1 py-0.5">
                <span className="text-[13px] font-semibold" style={{ color: col.labelColor }}>
                  {col.label}
                </span>
                <span
                  className="text-[11px] font-bold px-1.5 py-px rounded-full"
                  style={{ background: col.countBg, color: col.countColor }}
                >
                  {colEntries.length}
                </span>
                <button
                  type="button"
                  onClick={() => onAddEntry && onAddEntry(col.id)}
                  className="ml-auto w-5 h-5 flex items-center justify-center rounded hover:bg-white/60 text-[#98A2B3]"
                  aria-label={`Add entry to ${col.label}`}
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>

              <StrictModeDroppable droppableId={col.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="flex flex-col gap-2 min-h-[80px] rounded-[10px] transition-colors"
                    style={{ background: snapshot.isDraggingOver ? "rgba(34,131,255,.06)" : "transparent" }}
                  >
                    {colEntries.map((entry, index) => {
                      // Server-side a status change is an entry PATCH gated on
                      // the entry's agent / agency admin — dragging someone
                      // else's entry would only earn a 403 + rollback.
                      const modifiable = canModify ? canModify(entry) : false;
                      return (
                        <Draggable
                          key={entry.id}
                          draggableId={String(entry.id)}
                          index={index}
                          isDragDisabled={!modifiable}
                        >
                          {(dp) => (
                            <div ref={dp.innerRef} {...dp.draggableProps}>
                              <EntryCard
                                entry={entry}
                                dimmed={col.dimCards}
                                canModify={modifiable}
                                onStatusChange={onStatusChange}
                                onEdit={onEdit}
                                onDelete={onDelete}
                                onOpenThread={onOpenThread}
                                dragHandleProps={dp.dragHandleProps}
                              />
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                    {colEntries.length === 0 && !snapshot.isDraggingOver && (
                      <div className="text-[12px] text-[#98A2B3] text-center py-4">Drop here</div>
                    )}
                  </div>
                )}
              </StrictModeDroppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
};

export default BoardView;
